/**
 * BuzzTalk Realtime client — replaces Socket.io with Supabase Realtime.
 *
 * Browser subscribes directly to `postgres_changes` on Chat* tables. RLS
 * policies (`buzztalk_user_group_ids`, `buzztalk_is_workspace_owner`)
 * enforce that only group members + workspace OWNER see events. The server
 * is not in the WebSocket path — no Socket.io, no Render WS load, no
 * undici/Node 22 dependency.
 *
 * Auth: client uses the anon key for the initial connection, then upgrades
 * the session to a Supabase-compatible JWT signed by our server (see
 * `/api/chat/realtime-token`). That JWT carries `sub = userId`, so RLS
 * policies referencing `auth.uid()` resolve to the BuzzBit user and
 * postgres_changes events are filtered to rows the user can see.
 *
 * Call `authenticateRealtime()` once after BuzzBit login, then before any
 * `subscribeToGroup()`. The helper schedules a refresh 10 minutes before
 * the 1-hour token expires, so long-lived sessions stay authenticated
 * without exposing the JWT secret to the client.
 */

import { createClient, type RealtimeChannel, type SupabaseClient } from '@supabase/supabase-js';
import { logger } from '../../lib/logger';
import { chatApi } from './chatApi';

const SUPABASE_URL = (import.meta.env.VITE_SUPABASE_URL as string | undefined) ?? '';
const SUPABASE_ANON_KEY = (import.meta.env.VITE_SUPABASE_ANON_KEY as string | undefined) ?? '';

let _supabase: SupabaseClient | null = null;
function getSupabase(): SupabaseClient | null {
  if (_supabase) return _supabase;
  if (!SUPABASE_URL || !SUPABASE_ANON_KEY) {
    logger.debug('[realtime] VITE_SUPABASE_URL / VITE_SUPABASE_ANON_KEY not set — Realtime disabled');
    return null;
  }
  _supabase = createClient(SUPABASE_URL, SUPABASE_ANON_KEY, {
    auth: { persistSession: false, autoRefreshToken: false },
    realtime: { params: { eventsPerSecond: 10 } },
  });
  return _supabase;
}

export interface RealtimeMessageEvent {
  groupId: string;
  messageId: string;
  authorId: string | null;
  body: string;
  createdAt: string;
  kind: string;
}

export interface RealtimeReactionEvent {
  messageId: string;
  userId: string;
  emoji: string;
  action: 'add' | 'remove';
}

export interface RealtimeTaskEvent {
  groupId: string;
  taskId: string;
  status: string;
  action: 'created' | 'updated' | 'deleted';
}

type Handlers = {
  onMessage?: (event: RealtimeMessageEvent) => void;
  onReaction?: (event: RealtimeReactionEvent) => void;
  onTask?: (event: RealtimeTaskEvent) => void;
};

interface ChannelEntry {
  channel: RealtimeChannel;
  groupId: string;
}

const channels = new Map<string, ChannelEntry>();

interface PostgresChangePayload {
  new?: Record<string, unknown>;
  old?: Record<string, unknown>;
  eventType?: 'INSERT' | 'UPDATE' | 'DELETE';
}

export function subscribeToGroup(groupId: string, handlers: Handlers): () => void {
  const supabase = getSupabase();
  if (!supabase) return () => undefined;

  if (channels.has(groupId)) {
    return () => unsubscribeFromGroup(groupId);
  }

  const channel = supabase
    .channel(`chat:${groupId}`)
    .on(
      'postgres_changes' as never,
      { event: 'INSERT', schema: 'public', table: 'ChatMessage', filter: `groupId=eq.${groupId}` },
      (payload: PostgresChangePayload) => {
        const row = payload.new;
        if (!row || !handlers.onMessage) return;
        handlers.onMessage({
          groupId,
          messageId: String(row.id),
          authorId: row.authorId ? String(row.authorId) : null,
          body: String(row.body ?? ''),
          createdAt: String(row.createdAt),
          kind: String(row.kind ?? 'USER'),
        });
      },
    )
    .on(
      'postgres_changes' as never,
      { event: 'UPDATE', schema: 'public', table: 'ChatMessage', filter: `groupId=eq.${groupId}` },
      (payload: PostgresChangePayload) => {
        const row = payload.new;
        if (!row || !handlers.onMessage) return;
        handlers.onMessage({
          groupId,
          messageId: String(row.id),
          authorId: row.authorId ? String(row.authorId) : null,
          body: String(row.body ?? ''),
          createdAt: String(row.createdAt),
          kind: String(row.kind ?? 'USER'),
        });
      },
    )
    .on(
      'postgres_changes' as never,
      { event: 'INSERT', schema: 'public', table: 'ChatReaction' },
      (payload: PostgresChangePayload) => {
        const row = payload.new;
        if (!row || !handlers.onReaction) return;
        handlers.onReaction({
          messageId: String(row.messageId),
          userId: String(row.userId),
          emoji: String(row.emoji),
          action: 'add',
        });
      },
    )
    .on(
      'postgres_changes' as never,
      { event: 'DELETE', schema: 'public', table: 'ChatReaction' },
      (payload: PostgresChangePayload) => {
        const row = payload.old;
        if (!row || !handlers.onReaction) return;
        handlers.onReaction({
          messageId: String(row.messageId),
          userId: String(row.userId),
          emoji: String(row.emoji),
          action: 'remove',
        });
      },
    )
    .on(
      'postgres_changes' as never,
      { event: 'INSERT', schema: 'public', table: 'ChatTask', filter: `groupId=eq.${groupId}` },
      (payload: PostgresChangePayload) => {
        const row = payload.new;
        if (!row || !handlers.onTask) return;
        handlers.onTask({
          groupId,
          taskId: String(row.id),
          status: String(row.status ?? 'TODO'),
          action: 'created',
        });
      },
    )
    .on(
      'postgres_changes' as never,
      { event: 'UPDATE', schema: 'public', table: 'ChatTask', filter: `groupId=eq.${groupId}` },
      (payload: PostgresChangePayload) => {
        const row = payload.new;
        if (!row || !handlers.onTask) return;
        handlers.onTask({
          groupId,
          taskId: String(row.id),
          status: String(row.status ?? 'TODO'),
          action: 'updated',
        });
      },
    )
    .subscribe((status) => {
      logger.debug('[realtime] channel status', { groupId, status });
    });

  channels.set(groupId, { channel, groupId });
  return () => unsubscribeFromGroup(groupId);
}

export function unsubscribeFromGroup(groupId: string): void {
  const supabase = getSupabase();
  if (!supabase) return;
  const entry = channels.get(groupId);
  if (!entry) return;
  supabase.removeChannel(entry.channel).catch(() => undefined);
  channels.delete(groupId);
}

export function unsubscribeAll(): void {
  for (const groupId of Array.from(channels.keys())) {
    unsubscribeFromGroup(groupId);
  }
}

/**
 * Set the Supabase Auth session token. Required for RLS policies that
 * reference auth.uid(). Call after the BuzzBit /api/chat/realtime-token
 * endpoint returns a Supabase-compatible JWT.
 */
export async function setRealtimeSession(accessToken: string): Promise<void> {
  const supabase = getSupabase();
  if (!supabase) return;
  // realtime.setAuth — NOT auth.setSession. setSession() calls GET /auth/v1/user
  // to validate against the GoTrue auth server; our token is a custom Realtime
  // JWT (not a GoTrue user session), so that 403s. setAuth applies the JWT to the
  // Realtime socket for RLS with no auth-server round-trip.
  await supabase.realtime.setAuth(accessToken);
}

// Refresh handle so we can clear the pending timeout on logout / teardown.
let refreshTimer: ReturnType<typeof setTimeout> | null = null;
let lastAuthAt = 0;

/**
 * Fetch a fresh Realtime JWT from the BuzzBit server, apply it to the
 * Supabase client, and schedule a refresh ~10 minutes before expiry.
 *
 * Safe to call multiple times — the previous refresh timer is cleared
 * before scheduling the new one. If Realtime is disabled (missing
 * VITE_SUPABASE_URL / VITE_SUPABASE_ANON_KEY), this is a no-op.
 */
export async function authenticateRealtime(): Promise<boolean> {
  const supabase = getSupabase();
  if (!supabase) return false;
  try {
    const { token, expiresIn } = await chatApi.getRealtimeToken();
    // realtime.setAuth (not auth.setSession) — see setRealtimeSession above.
    await supabase.realtime.setAuth(token);
    lastAuthAt = Date.now();
    if (refreshTimer) clearTimeout(refreshTimer);
    // 10-minute safety buffer; minimum 30s so we never schedule in the past.
    const refreshInMs = Math.max(30_000, (expiresIn - 600) * 1000);
    refreshTimer = setTimeout(() => {
      authenticateRealtime().catch((err) => {
        logger.debug('[realtime] token refresh failed', { err });
      });
    }, refreshInMs);
    logger.debug('[realtime] authenticated', { expiresIn, refreshInMs });
    return true;
  } catch (err) {
    logger.debug('[realtime] auth failed', { err });
    return false;
  }
}

/**
 * Clear the scheduled refresh and drop the Supabase session. Call on
 * logout or app teardown so the Realtime WebSocket releases its
 * authenticated identity.
 */
export function clearRealtimeAuth(): void {
  if (refreshTimer) {
    clearTimeout(refreshTimer);
    refreshTimer = null;
  }
  const supabase = getSupabase();
  if (!supabase) return;
  // Reset Realtime auth back to the anon key — no GoTrue signOut (which would
  // hit /auth/v1/logout on a session that doesn't exist).
  void supabase.realtime.setAuth().catch(() => undefined);
  lastAuthAt = 0;
}

/** Visible for tests / debug overlays. */
export function getLastRealtimeAuthAt(): number {
  return lastAuthAt;
}
